import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import LocationOnRoundedIcon from '@mui/icons-material/LocationOnRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';
import AccountBalanceWalletRoundedIcon from '@mui/icons-material/AccountBalanceWalletRounded';
import MailOutlineRoundedIcon from '@mui/icons-material/MailOutlineRounded';
import LocalPhoneRoundedIcon from '@mui/icons-material/LocalPhoneRounded';
import { formatPricingLabel } from '../../utils/formatPrice';
import { navigateToStorefront } from '../../utils/storefrontPath';
import {
    ActionRow,
    BusinessName,
    CardHeader,
    ContactRow,
    EmptyActions,
    EmptyText,
    EmptyTitle,
    Eyebrow,
    Heading,
    InfoBar,
    InfoChip,
    Inner,
    Panel,
    PriceText,
    PrimaryButton,
    RankBadge,
    RecommendationCard,
    RecommendationList,
    RecommendationReason,
    RelatedServicesLabel,
    RequestSummary,
    RequestSummaryLabel,
    RequestSummaryText,
    SearchAgainLink,
    SecondaryButton,
    SubServiceCard,
    SubServiceDescription,
    SubServiceHeader,
    SubServiceList,
    SubServiceTagline,
    SubServiceTitle,
    Subheading,
} from './Recommendations.styled';
import { findSubserviceAcrossTrades } from '../../constants/mockData/allTrades';
import { resolveSubserviceTitle } from '../../utils/subserviceDisplay';
import ContactForServiceModal from '../common/ContactForServiceModal/ContactForServiceModal';
import {
    fingerprintWizardState,
    getPersistedWizardSearchRequestId,
    getLastWizardSearchRequestId,
    getWizardClientSessionId,
    getOrCreateWizardClientSessionId,
} from '../../utils/wizardClientSession';
import { persistActiveWizardSearchRequest } from '../../utils/persistActiveWizardSearchRequest';
import { serviceWizardSearchRequestService } from '../../services/api/serviceWizardSearchRequestService';
import { useAuth } from '../../context/AuthContext';
import {
    useWizardStateFromSearchRequestApi,
    mapServiceWizardSearchRequestToWizardState,
} from '../../hooks/useWizardStateFromSearchRequestApi';
import { getSubserviceDisplayLabel } from '../../utils/getSubserviceDisplayLabel';
import { resolveServiceTypeDisplayLabel } from '../../utils/serviceTypeDisplay';
import { storefrontInquiryMatchesWizardSearch } from '../../utils/storefrontInquiryWizardMatch';

/* Resolve a sub-service slug to its display label using the shared catalog.
   Falls back to the raw slug if it isn't in ALL_TRADES (e.g., new enum value
   the UI hasn't been updated for yet). */
const getSubServiceLabel = (serviceType, subOrSlug) => {
  if (subOrSlug && typeof subOrSlug === 'object') {
    return resolveSubserviceTitle(serviceType, subOrSlug);
  }
  const slug = subOrSlug;
  const match = findSubserviceAcrossTrades(slug);
  return match?.subservice?.label ?? slug;
};

/* Format "8595551212" → "859-555-1212" */
const formatPhone = (raw = '') =>
    String(raw)
        .replace(/\D/g, '')
        .replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3');

/* Map API availability slugs to display labels */
const AVAILABILITY_LABELS = {
    asap: 'Available ASAP',
    today: 'Available today',
    'few-days': 'Within a few days',
};

/** One ranked business: AI rationale, quick facts, related services, and contact actions. */
const RecommendationItem = ({ rec, rank, onViewStorefront, onSendInquiry }) => {
    const {
        BusinessName: bizName,
        BusinessAddress = {},
        BusinessEmail,
        BusinessPhoneNumber,
        SubServices = [],
        FinancingAvailable,
        Availability = [],
        AverageRating,
        ReviewCount,
        AI_Recommendation: aiRec,
    } = rec || {};

    const cityState = [BusinessAddress.City, BusinessAddress.State].filter(Boolean).join(', ');
    const reviewCount = Number(ReviewCount ?? 0);
    const hasReviews =
        reviewCount > 0 && AverageRating != null && !Number.isNaN(Number(AverageRating));
    const serviceType = rec?._parentServiceType || rec?.ServiceType;

    return (
        <RecommendationCard>
            <CardHeader>
                <RankBadge aria-label={`Rank ${rank}`}>{rank}</RankBadge>
                <BusinessName type="button" onClick={onViewStorefront}>
                    {bizName}
                </BusinessName>
            </CardHeader>

            {aiRec && <RecommendationReason>{aiRec}</RecommendationReason>}

            <InfoBar>
                {cityState && (
                    <InfoChip>
                        <LocationOnRoundedIcon aria-hidden />
                        {cityState}
                    </InfoChip>
                )}
                {hasReviews ? (
                    <InfoChip>
                        <StarRoundedIcon aria-hidden sx={{ color: '#f5b301' }} />
                        {Number(AverageRating).toFixed(1)} ({reviewCount})
                    </InfoChip>
                ) : (
                    <InfoChip>No reviews yet</InfoChip>
                )}
                {Availability.map((slot) => (
                    <InfoChip key={slot} $tone="blue">
                        <ScheduleRoundedIcon aria-hidden />
                        {AVAILABILITY_LABELS[slot] ?? slot}
                    </InfoChip>
                ))}
                {FinancingAvailable && (
                    <InfoChip $tone="green">
                        <AccountBalanceWalletRoundedIcon aria-hidden />
                        Financing available
                    </InfoChip>
                )}
            </InfoBar>

            {SubServices.length > 0 && (
                <>
                    <RelatedServicesLabel>Related services</RelatedServicesLabel>
                    <SubServiceList>
                        {SubServices.map((sub, subIdx) => {
                            const priceDisplay = formatPricingLabel(sub.Pricing, {
                                fixedPrefix: 'none',
                            });
                            return (
                                <SubServiceCard key={`${sub.SubServiceType}-${subIdx}`}>
                                    <SubServiceHeader>
                                        <SubServiceTitle>
                                            {getSubServiceLabel(serviceType, sub)}
                                        </SubServiceTitle>
                                        {priceDisplay && <PriceText>{priceDisplay}</PriceText>}
                                    </SubServiceHeader>
                                    {sub.Tagline && <SubServiceTagline>{sub.Tagline}</SubServiceTagline>}
                                    {sub.Description?.trim() && (
                                        <SubServiceDescription>
                                            {sub.Description.trim()}
                                        </SubServiceDescription>
                                    )}
                                </SubServiceCard>
                            );
                        })}
                    </SubServiceList>
                </>
            )}

            {(BusinessPhoneNumber || BusinessEmail) && (
                <ContactRow>
                    {BusinessPhoneNumber && (
                        <span>
                            <LocalPhoneRoundedIcon aria-hidden />
                            {formatPhone(BusinessPhoneNumber)}
                        </span>
                    )}
                    {BusinessEmail && (
                        <span>
                            <MailOutlineRoundedIcon aria-hidden />
                            {BusinessEmail}
                        </span>
                    )}
                </ContactRow>
            )}

            <ActionRow>
                <PrimaryButton onClick={onSendInquiry}>Send inquiry</PrimaryButton>
                <SecondaryButton variant="outlined" onClick={onViewStorefront}>
                    View storefront
                </SecondaryButton>
            </ActionRow>
        </RecommendationCard>
    );
};

/**
 * Recommendations list.
 * Accepts data from either:
 *  - react-router location.state.recommendations (preferred)
 *  - props.recommendations (handy for tests/storybook)
 */
const Recommendations = (props) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useAuth();

    const fromState =
        (location && location.state && location.state.recommendations) || null;
    const recommendations = useMemo(
        () => props.recommendations || fromState || [],
        [props.recommendations, fromState],
    );
    const customerDemand =
        location?.state?.customerDemand ||
        location?.state?.request?.CustomerDemand ||
        '';
    const [contactForServiceOpen, setContactForServiceOpen] = useState(false);
    const [pendingInquiryBusiness, setPendingInquiryBusiness] = useState(null);

    const staticWizardSearchRequestId = useMemo(() => {
        const fromStateId = location.state?.wizardSearchRequestId;
        if (fromStateId) return fromStateId;
        const w = location.state?.wizard;
        const fp = fingerprintWizardState(w);
        const match = getPersistedWizardSearchRequestId(fp);
        if (match) return match;
        if (w) return null;
        return getLastWizardSearchRequestId();
    }, [location.state]);

    const [recommendationsCreatedRequestId, setRecommendationsCreatedRequestId] = useState(null);
    const persistRecommendationsWizardRef = useRef(false);
    const wizardSearchRequestId = recommendationsCreatedRequestId ?? staticWizardSearchRequestId;

    const clientSessionIdForRequests = useMemo(() => {
        if (user) return null;
        try {
            return getOrCreateWizardClientSessionId();
        } catch {
            return null;
        }
    }, [user]);

    const routerWizard = location.state?.wizard;
    const { wizardState: wizardFromApi } = useWizardStateFromSearchRequestApi(
        Boolean(user),
        wizardSearchRequestId,
        routerWizard,
    );
    const effectiveWizard = routerWizard ?? wizardFromApi;
    const wizardFingerprint = useMemo(
        () => fingerprintWizardState(effectiveWizard),
        [effectiveWizard],
    );

    useEffect(() => {
        persistRecommendationsWizardRef.current = false;
    }, [wizardFingerprint]);

    useEffect(() => {
        const w = effectiveWizard;
        if (!w?.formData || !w.serviceType) {
            setRecommendationsCreatedRequestId(null);
            return undefined;
        }
        const fp = fingerprintWizardState(w);
        const existing = getPersistedWizardSearchRequestId(fp);
        if (existing) {
            setRecommendationsCreatedRequestId(existing);
            return undefined;
        }
        if (persistRecommendationsWizardRef.current) return undefined;
        persistRecommendationsWizardRef.current = true;

        let cancelled = false;
        (async () => {
            try {
                const id = await persistActiveWizardSearchRequest(w);
                if (cancelled) return;
                if (id) setRecommendationsCreatedRequestId(id);
            } catch (err) {
                console.error('Recommendations wizard search persist failed:', err);
                persistRecommendationsWizardRef.current = false;
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [effectiveWizard]);

    const contactModalSearchLabels = useMemo(() => {
        const w = effectiveWizard;
        if (!w) {
            return {
                serviceLabel: undefined,
                subserviceLabel: undefined,
                urgencyLabel: undefined,
            };
        }
        const serviceLabel = w.serviceType
            ? resolveServiceTypeDisplayLabel(w.serviceType)
            : undefined;
        const subserviceLabel = w.subServiceType
            ? getSubserviceDisplayLabel(w.subServiceType)
            : undefined;
        const urgencyLabel = w.requestSummary?.urgency;
        return { serviceLabel, subserviceLabel, urgencyLabel };
    }, [effectiveWizard]);

    const handleSendInquiryForRecommendation = async (rec) => {
        const serviceType = rec?._parentServiceType || rec?.ServiceType;
        const firstRecommendedSubservice = rec?.SubServices?.[0]?.SubServiceType;
        const subServiceType = rec?.SubServiceType != null
            ? String(rec.SubServiceType)
            : firstRecommendedSubservice != null
                ? String(firstRecommendedSubservice)
                : '';
        if (!serviceType || !subServiceType) return;

        let inquiryWizard = effectiveWizard;
        if (!inquiryWizard && wizardSearchRequestId) {
            try {
                const sid = getWizardClientSessionId();
                const doc = await serviceWizardSearchRequestService.getById(
                    wizardSearchRequestId,
                    {
                        clientSessionId: sid || undefined,
                    },
                );
                inquiryWizard = doc
                    ? mapServiceWizardSearchRequestToWizardState(doc)
                    : null;
            } catch (err) {
                console.error('Failed to load existing wizard context for inquiry:', err);
            }
        }

        const business = {
            id: rec.BusinessId,
            name: rec.BusinessName || '',
            phone: rec.BusinessPhoneNumber || '',
        };
        if (inquiryWizard && storefrontInquiryMatchesWizardSearch(inquiryWizard, rec)) {
            setPendingInquiryBusiness({ ...business, draft: null });
            setContactForServiceOpen(true);
            return;
        }

        // No matching saved search: the contact sheet asks timing, prefills the AI request as the
        // note, and saves the search request when sent.
        setPendingInquiryBusiness({
            ...business,
            draft: { serviceType, subServiceType, note: customerDemand || '' },
        });
        setContactForServiceOpen(true);
    };

    if (!recommendations.length) {
        return (
            <Panel aria-labelledby="recommendations-empty-title">
                <Inner>
                    <EmptyTitle id="recommendations-empty-title">
                        We couldn&apos;t find a match for that request
                    </EmptyTitle>
                    <EmptyText>
                        Try describing the job a different way, or use the guided search to browse
                        local pros by trade.
                    </EmptyText>
                    <EmptyActions>
                        <PrimaryButton component={RouterLink} to="/find-a-pro">
                            Use the guided search
                        </PrimaryButton>
                        <SecondaryButton
                            component={RouterLink}
                            to="/ai-service-search"
                            variant="outlined"
                        >
                            Try smart search again
                        </SecondaryButton>
                    </EmptyActions>
                </Inner>
            </Panel>
        );
    }

    return (
        <>
            <Panel aria-labelledby="recommendations-title">
                <Inner>
                    <Eyebrow>
                        <AutoAwesomeRoundedIcon aria-hidden />
                        Smart search
                    </Eyebrow>
                    <Heading id="recommendations-title">Top picks for your request</Heading>
                    <Subheading>
                        The best-matched service providers in Northern Kentucky, ranked for what
                        you described.
                    </Subheading>
                    {customerDemand ? (
                        <RequestSummary>
                            <RequestSummaryLabel>You asked for</RequestSummaryLabel>
                            <RequestSummaryText>{customerDemand}</RequestSummaryText>
                            <SearchAgainLink as={RouterLink} to="/ai-service-search">
                                New search
                            </SearchAgainLink>
                        </RequestSummary>
                    ) : null}

                    <RecommendationList>
                        {recommendations.map((rec, idx) => (
                            <RecommendationItem
                                key={rec?.BusinessId || idx}
                                rec={rec}
                                rank={idx + 1}
                                onViewStorefront={() => navigateToStorefront(navigate, rec)}
                                onSendInquiry={() => handleSendInquiryForRecommendation(rec)}
                            />
                        ))}
                    </RecommendationList>
                </Inner>
            </Panel>
            <ContactForServiceModal
                open={contactForServiceOpen}
                onClose={() => setContactForServiceOpen(false)}
                businessId={pendingInquiryBusiness?.id}
                businessName={pendingInquiryBusiness?.name}
                businessPhone={pendingInquiryBusiness?.phone}
                wizardSearchRequestId={
                    pendingInquiryBusiness?.draft ? undefined : wizardSearchRequestId
                }
                draftSearch={pendingInquiryBusiness?.draft || undefined}
                clientSessionId={clientSessionIdForRequests}
                user={user}
                serviceLabel={contactModalSearchLabels.serviceLabel}
                subserviceLabel={contactModalSearchLabels.subserviceLabel}
                urgencyLabel={contactModalSearchLabels.urgencyLabel}
                wizardState={effectiveWizard}
            />
        </>
    );
};

export default Recommendations;